import { DataGrid, GridPagination } from "@mui/x-data-grid";
import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
} from "@mui/material";
import MaterialBox from "../MaterialBox";
import SearchIcon from "@mui/icons-material/Search";
import { Info } from "@mui/icons-material";
import { DesignationContext } from "../../context/designationcontext";
import CustomizedPagination from "./components/CustomPagination";

function CustomPagination(props) {
  return <GridPagination ActionsComponent={CustomizedPagination} {...props} />;
}

const MaterialDataGrid = ({
  rows,
  columns,
  checkboxSelection,
  rowSelectionModel,
  onRowSelectionModelChange,
  onRowClick,
  loading,
  localeText,
  isSelectable,
  noSearch,
  subTab,
}) => {
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [internalSelectionModel, setInternalSelectionModel] = useState([]);
  const gridWrapperRef = useRef(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { filteredResults } = useContext(DesignationContext);

  useEffect(() => {
    if (searchText && searchText.length > 1) {
      const timeoutId = setTimeout(() => getSearchDetails(searchText), 300);
      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
    }
  }, [searchText, rows]);

  const scrolltoId = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView();
  };

  const getSearchDetails = async (text) => {
    setSearchResults([]);
    setIsLoading(true);
    scrolltoId("search");
    if (!hasSearched) setHasSearched(true);
    const results = rows.filter((row) =>
      Object.values(row).some((value) =>
        value?.toString().toLowerCase().includes(text.toLowerCase())
      )
    );
    setSearchResults(results);
    setIsLoading(false);
  };

  const columnsData = columns.map((column) => ({
    field: column.field,
    headerName:
      typeof column.headerName === "string" ? column.headerName : column.field,
    flex: column.flex,
    width: column.width,
    sortable: true,
    renderCell: (params) =>
      column.type === "status" ? (
        <Stack direction="row" alignItems="center" gap={1}>
          <Chip
            label={params.row.status?.label}
            size="small"
            icon={params.row.status?.icon || <Info />}
            color={params.row.status?.color}
            sx={{ fontSize: "11px", height: 20, borderRadius: "4px" }}
          />
        </Stack>
      ) : column.type === "images" ? (
        <img
          src={params.row.image}
          alt="item"
          style={{ width: 26, height: 26, borderRadius: 4, objectFit: "cover" }}
        />
      ) : (
        <span style={{ fontSize: "13px", color: "#2d3748" }}>
          {params.row[column.field]}
        </span>
      ),
  }));

  const displayRows =
    searchResults?.length > 0
      ? searchResults
      : filteredResults?.length > 0
        ? filteredResults
        : rows;

  return (
    <Box
      sx={{
        backgroundColor: "#eef1f6",
        borderRadius: "10px",
        overflow: "hidden",
        border: "1px solid #dde3ed",
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          px: 2,
          py: 1,
          backgroundColor: "#eef1f6",
          borderBottom: "1px solid #dde3ed",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {subTab && <MaterialBox>{subTab}</MaterialBox>}
        </Box>
        {!noSearch && (
          <TextField
            placeholder="Search..."
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            inputProps={{ maxLength: 50 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {isLoading ? (
                    <CircularProgress size={13} sx={{ color: "#8091a7" }} />
                  ) : (
                    <SearchIcon sx={{ fontSize: 15, color: "#0B1D51" }} />
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              width: 210,
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                fontSize: "12.5px",
                backgroundColor: "#fff",
                height: 32,
                "& fieldset": { borderColor: "#c8d0dc" },
                "&:hover fieldset": { borderColor: "#a0aec0" },
                "&.Mui-focused fieldset": { borderColor: "#2563eb", borderWidth: 1.5 },
              },
              "& input": {
                py: "5px",
                px: "6px",
                color: "#2d3748",
                "&::placeholder": { color: "#a0aec0", opacity: 1 },
              },
            }}
          />
        )}
      </Box>

      {/* DataGrid */}
      <Box
        ref={gridWrapperRef}
        sx={{ width: "100%", height: "calc(100vh - 235px)", minHeight: 340, backgroundColor: "#fff" }}
      >
        <DataGrid
          rows={displayRows}
          columns={columnsData}
          style={{ border: 0, fontSize: "13px" }}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          slots={{
            pagination: CustomPagination,
            loadingOverlay: (props) => (
              <MaterialBox style={{ overflow: "hidden" }}>
                <LinearProgress
                  sx={{ "& .MuiLinearProgress-bar": { backgroundColor: "#2563eb" }, backgroundColor: "#dbeafe" }}
                  {...props}
                />
              </MaterialBox>
            ),
          }}
          loading={loading}
          checkboxSelection={checkboxSelection}
          rowSelectionModel={rowSelectionModel || internalSelectionModel}
          onRowSelectionModelChange={(newSelectionModel) => {
            if (rowSelectionModel === undefined) setInternalSelectionModel(newSelectionModel);
            onRowSelectionModelChange?.(newSelectionModel);
          }}
          disableRowSelectionOnClick
          onRowClick={onRowClick}
          isSelectable={isSelectable}
          density="compact"
          rowHeight={44}
          columnHeaderHeight={40}
          autoHeight={false}
          disableSelectionOnClick
          localeText={localeText}
          sx={{
            border: 0,
            "& .MuiDataGrid-columnHeaders": { backgroundColor: "#2d3a5e", borderBottom: "none" },
            "& .MuiDataGrid-columnHeader": { backgroundColor: "#2d3a5e", outline: "none !important", "&:focus": { outline: "none" } },
            "& .MuiDataGrid-columnHeaderTitle": { color: "#ffffff", fontWeight: 600, fontSize: "12px", letterSpacing: "0.04em", textTransform: "uppercase" },
            "& .MuiDataGrid-sortIcon": { color: "rgba(255,255,255,0.7)", fontSize: "14px" },
            "& .MuiDataGrid-menuIconButton": { color: "rgba(255,255,255,0.5)" },
            "& .MuiDataGrid-iconButtonContainer": { visibility: "visible" },
            "& .MuiDataGrid-columnSeparator": { color: "rgba(255,255,255,0.12)", visibility: "visible" },
            "& .MuiDataGrid-row": {
              backgroundColor: "#fff",
              borderLeft: "3px solid transparent",
              transition: "background-color 0.12s ease, border-left-color 0.12s ease",
              "&:hover": {
                backgroundColor: "#dbeafe !important",
                borderLeftColor: "#2d3a5e !important",
                cursor: onRowClick ? "pointer" : "default",
              },
              "&.Mui-selected": {
                backgroundColor: "#dbeafe !important",
                borderLeftColor: "#1d4ed8 !important",
                "&:hover": { backgroundColor: "#bfdbfe !important" },
              },
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#f0f3fa",
            },
            "& .MuiDataGrid-cell": {
              padding: "0 18px",
              color: "#2d3748",
              fontSize: "13px",
              fontWeight: 400,
              borderBottom: "1px solid #e2e8f0",
              outline: "none !important",
              "&:focus": { outline: "none" },
              "&:focus-within": { outline: "none" },
            },
            "& .MuiDataGrid-footerContainer": { backgroundColor: "#eef1f6", borderTop: "1.5px solid #dde3ed", minHeight: 40 },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": { width: "6px", height: "6px" },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": { backgroundColor: "#f1f5f9" },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": { backgroundColor: "#c8d0dc", borderRadius: "3px", "&:hover": { backgroundColor: "#a0aec0" } },
            "& .MuiCheckbox-root": { color: "#a0aec0", padding: "4px", "& svg": { fontSize: "16px" }, "&.Mui-checked": { color: "#2563eb" }, "&.MuiCheckbox-indeterminate": { color: "#2563eb" } },
            "& .MuiDataGrid-overlay": { backgroundColor: "rgba(255,255,255,0.8)" },
          }}
        />
      </Box>
    </Box>
  );
};

MaterialDataGrid.propTypes = {
  rows: PropTypes.array,
  columns: PropTypes.array,
  checkboxSelection: PropTypes.bool,
  rowSelectionModel: PropTypes.array,
  onRowSelectionModelChange: PropTypes.func,
  isSelectable: PropTypes.bool,
  loading: PropTypes.bool,
  onRowClick: PropTypes.func,
  localeText: PropTypes.object,
};

export default MaterialDataGrid;
